<template>
  <div>
    <FavoriteMexc
      ref="favoriteMexc"
      @period-selected="handleSearch"
      @refresh-clicked="handleRefresh"
      @handle-fetchCoins="fetchCoins"
      @metrics-changed="handleMetrics"
      @coins-loaded="handleCoinsLoaded"
    />

    <!-- Chế độ Single Coin: hiển thị chart riêng cho 1 coin -->
    <template v-if="singleCoinSearchActive && selectedMetrics.length">
      <div class="chart-grid single-coin-search-chart">
        <div class="chart-header text-center">
          <h3>{{ singleSearchCoin }}</h3>
        </div>
        <div class="chart-box">
          <div class="chart-container first-chart" v-if="selectedMetrics.includes('openInterest') && singleSearchChartDataSets.length > 0">
            <canvas id="single-mexc-chart-0" key="single-mexc-chart-0" ref="single-mexc-chart-0"></canvas>
          </div>
          <div class="chart-container second-chart" v-if="selectedMetrics.includes('longShort') && singleSearchSecondChartDataSets.length > 0">
            <canvas id="single-mexc-second-chart-0" key="single-mexc-second-chart-0" ref="single-mexc-second-chart-0"></canvas>
          </div>
          <div class="chart-container third-chart" v-if="selectedMetrics.includes('longShort') && singleSearchThirdChartDataSets.length > 0">
            <canvas id="single-mexc-third-chart-0" key="single-mexc-third-chart-0" ref="single-mexc-third-chart-0"></canvas>
          </div>
          <div class="chart-container four-chart" v-if="selectedMetrics.includes('longShort') && singleSearchFourChartDataSets.length > 0">
            <canvas id="single-mexc-four-chart-0" key="single-mexc-four-chart-0" ref="single-mexc-four-chart-0"></canvas>
          </div>
        </div>
      </div>
    </template>

    <!-- Chế độ Favorites: hiển thị chart cho tất cả coin favorite -->
    <template v-if="!singleCoinSearchActive && selectedMetrics.length">
      <div
        v-for="index in totalChart"
        :key="'chart-grid-' + index"
        class="chart-grid"
      >
        <div class="chart-header text-center">
          <h3>
            Biểu đồ
            {{
              typeof arrayCoin[index - 1] === "object"
                ? arrayCoin[index - 1]?.coinName
                : arrayCoin[index - 1]
            }}
            <span
              v-if="typeof arrayCoin[index - 1] === 'object' && arrayCoin[index - 1]?.monitoringFlag === 'Y'"
              class="monitoring-badge"
            >
              Monitoring
            </span>
          </h3>
        </div>

        <div class="chart-box">
          <div
            class="chart-container first-chart"
            v-if="selectedMetrics.includes('openInterest')"
          >
            <canvas
              :id="chartIds[index - 1]"
              :key="chartIds[index - 1]"
              :ref="'chart' + (index - 1)"
            ></canvas>
          </div>

          <div
            class="chart-container second-chart"
            v-if="selectedMetrics.includes('longShort')"
          >
            <canvas
              :id="secondChartIds[index - 1]"
              :key="secondChartIds[index - 1]"
              :ref="'second-chart' + (index - 1)"
            ></canvas>
          </div>

          <div
            class="chart-container third-chart"
            v-if="selectedMetrics.includes('longShort')"
          >
            <canvas
              :id="thirdChartIds[index - 1]"
              :key="thirdChartIds[index - 1]"
              :ref="'third-chart' + (index - 1)"
            ></canvas>
          </div>

          <div
            class="chart-container four-chart"
            v-if="selectedMetrics.includes('longShort')"
          >
            <canvas
              :id="fourChartIds[index - 1]"
              :key="fourChartIds[index - 1]"
              :ref="'four-chart' + (index - 1)"
            ></canvas>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { API_URL } from "../config";
import {
  Chart as ChartJS,
  CategoryScale,
  TimeScale,
  BarController,
  BarElement,
  LinearScale,
  LineElement,
  PointElement,
  LineController,
  Title,
  Tooltip,
  Legend,
  Filler,
  SubTitle,
} from "chart.js";
import "chartjs-adapter-date-fns";
import '@/assets/css/mexc.css'
import "vue-select/dist/vue-select.css";
import ChartDataLabels from "chartjs-plugin-datalabels";
import FavoriteMexc from './favorite/FavoriteMexc.vue'
ChartJS.register(
  CategoryScale,
  SubTitle,
  TimeScale,
  BarController,
  BarElement,
  LinearScale,
  LineElement,
  PointElement,
  LineController,
  ChartDataLabels,
  Title,
  Tooltip,
  Legend,
  Filler
);
const nfComma = new Intl.NumberFormat('en-US');
function fmtComma(n, frac = 0) {
  if (!Number.isFinite(n)) return '-';
  return nfComma.format(frac ? Number(n).toFixed(frac) : Math.round(n));
}
function niceMinMax(arr, pad = 0.06) {
  const vals = (arr || []).filter(Number.isFinite);
  if (!vals.length) return { min: 0, max: 1 };
  let min = Math.min(...vals);
  let max = Math.max(...vals);
  if (min === max) {
    const bump = Math.abs(min) || 1;
    return { min: min - bump * pad, max: max + bump * pad };
  }
  const range = max - min;
  return { min: min - range * pad, max: max + range * pad };
}
const shadowLine = {
  id: 'shadowLine',
  afterDatasetsDraw(chart) {
    const { ctx, data } = chart
    const meta = chart.getDatasetMeta(1)
    if (!meta || !meta.dataset) return
    ctx.save()
    ctx.shadowColor = 'rgba(0,0,0,0.7)'
    ctx.shadowBlur = 6
    ctx.shadowOffsetY = 2
    ctx.lineWidth = 2
    ctx.strokeStyle = '#ffffff'
    ctx.beginPath()
    meta.dataset.draw(ctx, data)
    ctx.restore()
  }
}
const stripeBg = {
  id: 'stripeBgLight',
  beforeDraw(chart) {
    const y = chart.scales?.y;
    const area = chart.chartArea;
    if (!y || !y.ticks?.length || !area) return;

    const { ctx, chartArea: { left, right, top, bottom } } = chart;

    ctx.save();
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(left, top, right - left, bottom - top);

    for (let i = 0; i < y.ticks.length - 1; i++) {
      if (i % 2 === 0) {
        const y1 = y.getPixelForTick(i);
        const y2 = y.getPixelForTick(i + 1);
        ctx.fillStyle = 'rgba(0,0,0,0.035)';
        ctx.fillRect(left, y2, right - left, y1 - y2);
      }
    }
    ctx.restore();
  }
};
ChartJS.register(shadowLine)
export default {
  name: "MexcCoin",
  components: {
    FavoriteMexc
  },
  data() {
    return {
      selectedCoin: null,
      chartIds: Array.from({ length: 30 }, (_, i) => `chart-${i}`),
      secondChartIds: Array.from({ length: 30 }, (_, i) => `second-chart-${i}`),
      thirdChartIds: Array.from({ length: 30 }, (_, i) => `third-chart-${i}`),
      fourChartIds: Array.from({ length: 30 }, (_, i) => `four-chart-${i}`),
      fiveChartIds: Array.from({ length: 30 }, (_, i) => `five-chart-${i}`),
      sixChartIds: Array.from({ length: 30 }, (_, i) => `six-chart-${i}`),
      sevenChartIds: Array.from({ length: 30 }, (_, i) => `seven-chart-${i}`),
      coinNames: Array(20).fill(""),
      periods: "",
      selectedMetrics: ['openInterest','longShort','inflow30d','inflow24h','fundingRate'],
      initialMetrics: ['openInterest','longShort','inflow30d','inflow24h','fundingRate'],
      charts: [],
      secondCharts: [],
      thirdCharts: [],
      fourCharts: [],
      fiveCharts: [],
      sixCharts: [],
      sevenCharts: [],
      chartDataSets: [],
      secondChartDataSets: [],
      thirdChartDataSets: [],
      fourChartDataSets: [],
      fiveChartDataSets: [],
      sixChartDataSets: [],
      sevenChartDataSets: [],
      loading: false,
      coins: [],
      totalChart: 0,
      arrayCoin: [],
      displayedCoins: [],
      exceptionSymbols: new Set(["1000CAT", "1000CATUSDT", "1000CHEEMS", "1000CHEEMSUSDT", "1000SATS", "1000SATSUSDT"]),
      // Single-coin mode state
      singleCoinSearchActive: false,
      singleSearchCoin: "",
      singleSearchChartDataSets: [],
      singleSearchSecondChartDataSets: [],
      singleSearchThirdChartDataSets: [],
      singleSearchFourChartDataSets: [],
      singleSearchCharts: [],
      singleSearchSecondCharts: [],
      singleSearchThirdCharts: [],
      singleSearchFourCharts: [],
    };
  },
  async created() {
    // Không gọi fetchCoins ở đây nữa vì con (FavoriteMexc) sẽ lo việc này khi nó khởi tạo
  },
  async mounted() {
    // Kiểm tra nếu URL có coinName khi vào trang lần đầu
    if (this.$route.params.coinName) {
      if (this.coins.length === 0) {
        await this.fetchCoins();
      }
      await this.handleSingleCoinSearch({
        coin: { coinName: this.$route.params.coinName },
        period: this.periods || '5m'
      });
      if (this.$refs.favoriteMexc) {
        this.$refs.favoriteMexc.selectedCoin = this.$route.params.coinName;
      }
    }
  },
  watch: {
    '$route.params.coinName': {
      async handler(newVal) {
        console.log('🔄 Mexc route changed:', newVal);
        if (newVal) {
          // Có coinName → chế độ single coin
          if (this.coins.length === 0) {
            await this.fetchCoins();
          }
          this.singleCoinSearchActive = true;
          await this.handleSingleCoinSearch({
            coin: { coinName: newVal },
            period: this.periods || '5m'
          });
        } else {
          // Không có coinName → chế độ danh sách favorites
          console.log('🔙 Back to favorites list');
          this.singleCoinSearchActive = false;
          this.singleSearchCoin = '';
          this.singleSearchChartDataSets = [];
          this.singleSearchSecondChartDataSets = [];
          this.singleSearchThirdChartDataSets = [];
          this.singleSearchFourChartDataSets = [];
        }
      }
    }
  },
  methods: {
    getCoinName(coinItem) {
      if (typeof coinItem === "string") {
        return coinItem;
      }
      return coinItem?.coinName || "BTC";
    },
    normalizeCoinName(coinName) {
      if (!coinName) return "";
      const upper = coinName.toUpperCase();
      if (upper === "1000CAT" || upper === "1000CATUSDT") {
        return coinName;
      }
      if (this.exceptionSymbols.has(coinName)) {
        return coinName;
      }
      return coinName.replace(/^\d+/, '');
    },
    handleCoinsLoaded(data) {
      console.log("📦 Mexc Parent received coins-loaded event");
      this.processCoinsData(data);
    },

    processCoinsData(data) {
      this.coins = data.map((coin) => ({
        id: coin.id,
        coinName: `${coin.coinName}USDT`,
        favoriteFlag: coin.favoriteFlag,
        monitoringFlag: coin.monitoringFlag,
      }));
      const allCoins = this.coins
        .map((coin) => ({ ...coin, isSelected: coin.favoriteFlag === "1" }))
        .sort((a, b) => b.isSelected - a.isSelected);
      this.coinNames = allCoins
        .slice(0, 20)
        .map((coin) => {
          if (coin.isSelected) {
            return {
              coinName: coin.coinName,
              monitoringFlag: coin.monitoringFlag
            };
          }
          return "";
        });
      this.coinNames = [
        ...this.coinNames,
        ...Array(20 - this.coinNames.length).fill(""),
      ];
      this.displayedCoins = allCoins.slice(0, 20);
    },

    async fetchCoins() {
      try {
        const response = await fetch(`${API_URL}/mexc/get-coins`);
        const data = await response.json();
        this.processCoinsData(data);
      } catch (error) {
        console.error("Error fetching coins:", error);
      }
    },

    async handleSearch(period) {
      this.periods = period;

      // Nếu đang ở chế độ single coin → refresh riêng coin đó
      if (this.singleCoinSearchActive && this.singleSearchCoin) {
        await this.handleSingleCoinSearch({ coin: { coinName: this.singleSearchCoin }, period });
        return;
      }

      if (this.periods == "") {
        this.$toast.open({
          message: 'Vui lòng chọn thời gian!',
          type: 'error',
          position: 'top-right',
        });
        return false;
      }
      const loading = this.$loading.show();

      // Cập nhật danh sách coin từ API (để lấy monitoringFlag mới nhất)
      await this.fetchCoins();

      const filteredArray = this.coinNames
        .filter((item) => item != null && item !== "");
      this.coinNames = filteredArray;

      for (let i = 0; i < filteredArray.length; i++) {
        this.loading = true;
        const jobs = [];
        const coinName = typeof this.coinNames[i] === "string" ? this.coinNames[i] : this.coinNames[i].coinName;
        jobs.push(this.fetchNotionalData(coinName, i, this.periods));
        jobs.push(
          this.fetchSecondApiDataForCoin(coinName, i, this.periods),
          this.fetchThirdApiDataForCoin(coinName, i, this.periods),
          this.fetchFourApiDataForCoin(coinName, i, this.periods)
        );
        await Promise.all(jobs);
      }
      this.totalChart = filteredArray.length;
      this.loading = false;
      this.arrayCoin = this.coinNames;
      this.createCharts();
      loading.hide();
    },

    async handleRefresh(period) {
      this.periods = period;

      // Nếu đang ở chế độ single coin → refresh riêng coin đó
      if (this.singleCoinSearchActive && this.singleSearchCoin) {
        await this.handleSingleCoinSearch({ coin: { coinName: this.singleSearchCoin }, period });
        return;
      }

      if (this.periods == "") {
        this.$toast.open({
          message: 'Vui lòng chọn thời gian!',
          type: 'error',
          position: 'top-right',
        });
        return false;
      }
      const loading = this.$loading.show();

      // Cập nhật danh sách coin từ API (để lấy monitoringFlag mới nhất)
      await this.fetchCoins();

      const filteredArray = this.coinNames
        .filter((item) => item != null && item !== "");
      this.coinNames = filteredArray;

      for (let i = 0; i < filteredArray.length; i++) {
        this.loading = true;
        const jobs = [];
        const sel = new Set(this.selectedMetrics);
        const coinName = typeof this.coinNames[i] === "string" ? this.coinNames[i] : this.coinNames[i].coinName;
        if (sel.has('openInterest')) jobs.push(this.fetchNotionalData(coinName, i, this.periods));
        if (sel.has('longShort')) {
          jobs.push(
            this.fetchSecondApiDataForCoin(coinName, i, this.periods),
            this.fetchThirdApiDataForCoin(coinName, i, this.periods),
            this.fetchFourApiDataForCoin(coinName, i, this.periods)
          );
        }
        await Promise.all(jobs);
      }
      this.totalChart = filteredArray.length;
      this.loading = false;
      this.arrayCoin = this.coinNames;
      this.createCharts();
      loading.hide();
    },

    // ---- SINGLE COIN METHODS ----

    async handleSingleCoinSearch(payload) {
      const { coin, period } = payload;
      const coinName = coin.coinName.endsWith('USDT') ? coin.coinName : `${coin.coinName}USDT`;
      this.singleSearchCoin = coinName;
      this.singleCoinSearchActive = true;
      if (period) this.periods = period;

      const loading = this.$loading.show();
      try {
        const jobs = [];
        const sel = new Set(this.selectedMetrics);
        if (sel.has('openInterest')) jobs.push(this.fetchSingleCoinNotionalData(coinName, this.periods));
        if (sel.has('longShort')) {
          jobs.push(
            this.fetchSingleCoinSecondApiData(coinName, this.periods),
            this.fetchSingleCoinThirdApiData(coinName, this.periods),
            this.fetchSingleCoinFourApiData(coinName, this.periods)
          );
        }
        await Promise.all(jobs);
        this.createSingleCoinCharts();
      } catch (error) {
        console.error('Error fetching single coin data:', error);
        this.$toast.open({ message: 'Lỗi khi tải dữ liệu coin!', type: 'error', position: 'top-right' });
      } finally {
        loading.hide();
      }
    },

    async fetchSingleCoinNotionalData(coinName, period) {
      try {
        const res = await fetch(`${API_URL}/mexc/open-interest`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: coinName, periodMinutes: period }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const raw = await res.json();
        const list = Array.isArray(raw) ? raw
          : Array.isArray(raw?.data) ? raw.data
          : Array.isArray(raw?.data?.list) ? raw.data.list : [];
        if (!list.length) { this.singleSearchChartDataSets = []; return; }
        const sorted = [...list].sort((a, b) => Number(a.timestamp) - Number(b.timestamp));
        this.singleSearchChartDataSets = sorted.slice(-30).map(item => ({
          x: Number(item.timestamp),
          oi: Number(item.sumOpenInterest),
          notional: Number(item.sumOpenInterestValue),
        }));
      } catch (error) {
        console.error(`Lỗi OI cho ${coinName}:`, error?.message || error);
        this.singleSearchChartDataSets = [];
      }
    },

    async fetchSingleCoinSecondApiData(coinName, period) {
      try {
        const res = await fetch(`${API_URL}/mexc/longshort-acc`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ symbol: coinName, period, limit: 30 }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        this.singleSearchSecondChartDataSets = Array.isArray(data)
          ? data.filter(i => i.timeGet).map(i => ({
              x: i.timeGet, y: parseFloat(parseFloat(i.longShortRatio || 0).toFixed(3)),
              longPercent: parseFloat(i.longPercent),
              shortPercent: parseFloat(i.shortPercent),
              longShortRatio: parseFloat(parseFloat(i.longShortRatio || 0).toFixed(3)),
            })) : [];
      } catch (error) {
        console.error(`Lỗi Long/Short Acc cho ${coinName}:`, error?.message || error);
        this.singleSearchSecondChartDataSets = [];
      }
    },

    async fetchSingleCoinThirdApiData(coinName, period) {
      try {
        const res = await fetch(`${API_URL}/mexc/longshort-poss`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ symbol: coinName, period, limit: 30 }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        this.singleSearchThirdChartDataSets = Array.isArray(data)
          ? data.filter(i => i.timeGet).map(i => ({
              x: i.timeGet, y: parseFloat(parseFloat(i.longShortRatio || 0).toFixed(3)),
              longPercent: parseFloat(i.longPercent),
              shortPercent: parseFloat(i.shortPercent),
              longShortRatio: parseFloat(parseFloat(i.longShortRatio || 0).toFixed(3)),
            })) : [];
      } catch (error) {
        console.error(`Lỗi Long/Short Poss cho ${coinName}:`, error?.message || error);
        this.singleSearchThirdChartDataSets = [];
      }
    },

    async fetchSingleCoinFourApiData(coinName, period) {
      try {
        const res = await fetch(`${API_URL}/mexc/longshort`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ symbol: coinName, period, limit: 30 }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        this.singleSearchFourChartDataSets = Array.isArray(data)
          ? data.filter(i => i.timeGet).map(i => ({
              x: i.timeGet, y: parseFloat(parseFloat(i.longShortRatio || 0).toFixed(3)),
              longPercent: parseFloat(i.longPercent),
              shortPercent: parseFloat(i.shortPercent),
              longShortRatio: parseFloat(parseFloat(i.longShortRatio || 0).toFixed(3)),
            })) : [];
      } catch (error) {
        console.error(`Lỗi Long/Short cho ${coinName}:`, error?.message || error);
        this.singleSearchFourChartDataSets = [];
      }
    },

    createSingleCoinCharts() {
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          const kill = c => { try { c && !c._destroyed && c.destroy(); } catch (_) { /* ignore */ } };
          [this.singleSearchCharts, this.singleSearchSecondCharts, this.singleSearchThirdCharts, this.singleSearchFourCharts]
            .forEach(arr => (arr || []).forEach(c => kill(c)));
          this.singleSearchCharts = [];
          this.singleSearchSecondCharts = [];
          this.singleSearchThirdCharts = [];
          this.singleSearchFourCharts = [];

          let periods = this.periods;
          const timeUnit = (p) => (p === '1h' || p === '4h') ? 'hour' : p === '1d' ? 'day' : 'minute';

          // Chart 1: Open Interest
          const canvas1 = document.getElementById('single-mexc-chart-0');
          if (canvas1 && this.singleSearchChartDataSets.length > 0) {
            const exist = ChartJS.getChart(canvas1);
            if (exist) try { exist.destroy(); } catch (_) { /* ignore */ }
            const ctx = canvas1.getContext('2d');
            if (ctx) {
              const dataSet = this.singleSearchChartDataSets;
              const oi = dataSet.map(item => item.oi);
              const notional = dataSet.map(item => item.notional);
              const labels = dataSet.map(item => item.x);
              const yL = niceMinMax(oi, 0.06);
              const yR = niceMinMax(notional, 0.06);
              this.singleSearchCharts[0] = new ChartJS(ctx, {
                type: 'bar',
                data: {
                  labels,
                  datasets: [
                    { type: 'bar', label: `Open Interest (${this.singleSearchCoin.replace('USDT','')})`, data: oi, yAxisID: 'y', backgroundColor: '#f0b90b', borderColor: '#dca600', borderSkipped: false, borderWidth: 0, borderRadius: 2, barPercentage: 0.6, categoryPercentage: 0.8, maxBarThickness: 12, datalabels: { display: false }, order: 1 },
                    { type: 'line', label: 'Notional Value (USDT)', data: notional, yAxisID: 'y1', borderColor: '#ffffff', borderWidth: 2, order: 999, showLine: true, spanGaps: true, pointRadius: 2, pointHoverRadius: 3, pointBackgroundColor: '#0b1220', pointBorderColor: '#e5e7eb', tension: 0.25, fill: false, datalabels: { display: false } }
                  ],
                },
                options: {
                  responsive: true, maintainAspectRatio: false, interaction: { mode: 'index', intersect: false },
                  plugins: { legend: { display: true }, datalabels: { display: false }, title: { display: true, text: `Open Interest (${this.singleSearchCoin})` }, tooltip: { backgroundColor: '#111827', borderColor: '#374151', borderWidth: 1, titleColor: '#e5e7eb', bodyColor: '#e5e7eb', callbacks: { title(items) { const ts = items[0]?.parsed.x; return ts ? new Date(ts).toLocaleTimeString() : ''; }, label(c) { return ` ${c.dataset.label}: ${fmtComma(c.parsed.y)}`; } } } },
                  scales: {
                    x: { type: 'time', time: { unit: timeUnit(periods) }, grid: { color: 'rgba(255,255,255,0.10)' }, ticks: { color: '#9ca3af', maxTicksLimit: 15, maxRotation: 55, minRotation: 35, callback(value) { const d = new Date(value); return periods === '1d' ? d.toLocaleDateString('en-GB',{day:'2-digit',month:'2-digit'}) : d.toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit'}); } } },
                    y: { position: 'left', min: yL.min, max: yL.max, beginAtZero: false, grid: { color: 'rgba(255,255,255,0.10)' }, ticks: { color: '#9ca3af', font: { size: 11 }, callback(v) { const a=Math.abs(v); if(a>=1e9) return (v/1e9).toFixed(0)+'B'; if(a>=1e6) return (v/1e6).toFixed(0)+'M'; if(a>=1e3) return (v/1e3).toFixed(0)+'K'; return Math.round(v); } } },
                    y1: { position: 'right', min: yR.min, max: yR.max, beginAtZero: false, grid: { display: false }, ticks: { color: '#9ca3af', font: { size: 11 }, callback(v) { const a=Math.abs(v); if(a>=1e9) return (v/1e9).toFixed(0)+'B'; if(a>=1e6) return (v/1e6).toFixed(0)+'M'; if(a>=1e3) return (v/1e3).toFixed(0)+'K'; return Math.round(v); } } }
                  }
                },
                plugins: [stripeBg]
              });
              if (canvas1.parentNode) canvas1.parentNode.style.height = '300px';
            }
          }

          // Helper render Long/Short chart
          const renderLSChart = (canvasId, dataSet, title, chartArr, arrIdx) => {
            const canvas = document.getElementById(canvasId);
            if (!canvas || !dataSet || !dataSet.length) return;
            const exist = ChartJS.getChart(canvas);
            if (exist) try { exist.destroy(); } catch (_) { /* ignore */ }
            const ctx = canvas.getContext('2d');
            if (!ctx) return;
            const minVal = Math.min(...dataSet.map(i => i.longShortRatio));
            const rounded = Math.floor(minVal / 0.05) * 0.05;
            const startValue = (minVal - rounded <= 0.05) ? rounded : rounded + 0.05;
            chartArr[arrIdx] = new ChartJS(ctx, {
              type: 'bar',
              data: {
                datasets: [
                  { label: 'Long/Short Ratio', type: 'line', data: dataSet, borderColor: 'rgba(0,0,0,1)', pointBackgroundColor: 'rgba(255,255,255,1)', pointBorderColor: 'rgba(0,0,0,1)', pointBorderWidth: 1.5, pointRadius: 4, pointHoverRadius: 6, fill: true, tension: 0.4, borderWidth: 2, yAxisID: 'y-line', meta: { customValue: dataSet } },
                  { label: '% Long', data: dataSet.map(i => ({ x: i.x, y: i.longPercent })), backgroundColor: 'rgba(0,209,112,0.8)', stack: 'Stack 0', yAxisID: 'y-bar', meta: { customValue: dataSet } },
                  { label: '% Short', data: dataSet.map(i => ({ x: i.x, y: i.shortPercent })), backgroundColor: 'rgba(231,76,60,0.8)', stack: 'Stack 0', yAxisID: 'y-bar', meta: { customValue: dataSet } },
                ],
              },
              options: {
                responsive: true, maintainAspectRatio: false,
                plugins: { legend: { display: true }, datalabels: { display: false }, title: { display: true, text: title }, tooltip: { callbacks: { label(tooltipItem) { const d = tooltipItem.dataset.meta.customValue[tooltipItem.dataIndex]; return `Long: ${d.longPercent}%\nShort: ${d.shortPercent}%\nRatio: ${Number(d.longShortRatio).toFixed(3)}`; } } } },
                scales: {
                  x: { type: 'time', time: { unit: timeUnit(periods) }, labels: dataSet.map(i => i.x), ticks: { maxTicksLimit: 20, callback(value) { const d = new Date(value); return periods === '1d' ? d.toLocaleDateString('en-GB',{day:'2-digit',month:'2-digit'}) : d.toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit'}); } }, stacked: true },
                  'y-line': { position: 'right', grid: { drawOnChartArea: false }, beginAtZero: false, min: startValue, ticks: { callback: v => v.toFixed(2) } },
                  'y-bar': { position: 'left', stacked: true, ticks: { callback: v => `${v}%` } },
                  y: { display: false }
                }
              }
            });
            if (canvas.parentNode) canvas.parentNode.style.height = '300px';
          };

          renderLSChart('single-mexc-second-chart-0', this.singleSearchSecondChartDataSets, `Top Accounts (${this.singleSearchCoin})`, this.singleSearchSecondCharts, 0);
          renderLSChart('single-mexc-third-chart-0', this.singleSearchThirdChartDataSets, `Top Positions (${this.singleSearchCoin})`, this.singleSearchThirdCharts, 0);
          renderLSChart('single-mexc-four-chart-0', this.singleSearchFourChartDataSets, `Long/Short (${this.singleSearchCoin})`, this.singleSearchFourCharts, 0);
        });
      });
    },

    async fetchNotionalData(coinName, index, period) {
      try {
        const requestBody = {
          name: coinName,
          periodMinutes: period,
        };

        const res = await fetch(`${API_URL}/mexc/open-interest`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody),
        });

        if (!res.ok) {
          const text = await res.text().catch(() => '');
          throw new Error(`HTTP ${res.status} ${res.statusText} - ${text}`);
        }

        const raw = await res.json();
        const list =
          Array.isArray(raw) ? raw
            : Array.isArray(raw?.data) ? raw.data
              : Array.isArray(raw?.data?.list) ? raw.data.list
                : [];

        if (!Array.isArray(list) || list.length === 0) {
          console.warn('Payload rỗng hoặc sai định dạng:', raw);
          this.chartDataSets[index] = [];
          return;
        }
        const sorted = [...list].sort((a, b) => Number(a.timestamp) - Number(b.timestamp));
        const last = sorted.slice(-30);
        const result = last.map(item => ({
          x: Number(item.timestamp),
          oi: Number(item.sumOpenInterest),
          notional: Number(item.sumOpenInterestValue),
        }));

        this.chartDataSets[index] = result;
      } catch (error) {
        console.error(`Lỗi khi tải dữ liệu từ API mới cho ${coinName}:`, error?.message || error);
        this.chartDataSets[index] = [];
      }
    },

    async fetchSecondApiDataForCoin(coinName, index, period) {
      try {
        const requestBody = {
          symbol: coinName,
          period: period,
          limit: 30,
        };

        const response = await fetch(
          `${API_URL}/mexc/longshort-acc`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(requestBody),
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        const result = data
          .map((item) => {
            if (item.timeGet) {
              return {
                x: item.timeGet,
                y: parseFloat(parseFloat(item.longShortRatio || 0).toFixed(3)),
                longPercent: parseFloat(item.longPercent),
                shortPercent: parseFloat(item.shortPercent),
                longShortRatio: parseFloat(parseFloat(item.longShortRatio || 0).toFixed(3)),
              };
            }
            return null;
          })
          .filter((item) => item !== null);
        this.secondChartDataSets[index] = result;
      } catch (error) {
        console.error(
          `Lỗi khi tải dữ liệu từ API mới cho ${coinName}:`,
          error.message || error
        );
      }
    },

    async fetchThirdApiDataForCoin(coinName, index, period) {
      try {
        const requestBody = {
          symbol: coinName,
          period: period,
          limit: 30,
        };

        const response = await fetch(
          `${API_URL}/mexc/longshort-poss`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(requestBody),
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        const result = data
          .map((item) => {
            if (item.timeGet) {
              return {
                x: item.timeGet,
                y: parseFloat(parseFloat(item.longShortRatio || 0).toFixed(3)),
                longPercent: parseFloat(item.longPercent),
                shortPercent: parseFloat(item.shortPercent),
                longShortRatio: parseFloat(parseFloat(item.longShortRatio || 0).toFixed(3)),
              };
            }
            return null;
          })
          .filter((item) => item !== null);

        this.thirdChartDataSets[index] = result;
      } catch (error) {
        console.error(
          `Lỗi khi tải dữ liệu từ API thứ ba cho ${coinName}:`,
          error.message || error
        );
      }
    },
    async fetchFourApiDataForCoin(coinName, index, period) {
      try {
        const requestBody = {
          symbol: coinName,
          period: period,
          limit: 30,
        };

        const response = await fetch(`${API_URL}/mexc/longshort`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestBody),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        const result = data
          .map((item) => {
            if (item.timeGet) {
              return {
                x: item.timeGet,
                y: parseFloat(parseFloat(item.longShortRatio || 0).toFixed(3)),
                longPercent: parseFloat(item.longPercent),
                shortPercent: parseFloat(item.shortPercent),
                longShortRatio: parseFloat(parseFloat(item.longShortRatio || 0).toFixed(3)),
              };
            }
            return null;
          })
          .filter((item) => item !== null);

        this.fourChartDataSets[index] = result;
      } catch (error) {
        console.error(
          `Lỗi khi tải dữ liệu từ API thứ ba cho ${coinName}:`,
          error.message || error
        );
      }
    },
    // async fetchFiveApiDataForCoin(coinName, index) {
    //   try {
    //     const requestBody = {
    //       symbol: this.normalizeCoinName(coinName),
    //       groupCount: '7',
    //       groupSize: '24',
    //       period: 'HOUR_1',
    //       volumeType: 'BIG',
    //     };

    //     const res = await fetch(`${API_URL}/binance/day-large-inflow`, {
    //       method: 'POST',
    //       headers: { 'Content-Type': 'application/json' },
    //       body: JSON.stringify(requestBody),
    //     });

    //     if (!res.ok) {
    //       const t = await res.text().catch(() => '');
    //       throw new Error(`HTTP ${res.status} ${res.statusText} - ${t}`);
    //     }

    //     const raw = await res.json();
    //     const data = raw?.data ?? raw;
    //     const list = Array.isArray(data?.netCapitalInList) ? data.netCapitalInList : [];
    //     let series = list
    //       .map(it => Number.parseFloat(it?.netCapitalInflowVolume ?? it?.value ?? 0))
    //       .filter(Number.isFinite);
    //     if (series.length > 30) {
    //       series = series.slice(-30);
    //     } else if (series.length < 30) {
    //       series = Array(30 - series.length).fill(0).concat(series);
    //     }
    //     const totalInFlowNum = Number.parseFloat(data?.totalInFlow);
    //     const totalInFlow = Number.isFinite(totalInFlowNum)
    //       ? totalInFlowNum
    //       : series.reduce((sum, v) => sum + (Number.isFinite(v) ? v : 0), 0);
    //     const totalInFlowUsdtNum = Number.parseFloat(data?.totalInFlowUsdt);
    //     const totalInFlowUsdt = Number.isFinite(totalInFlowUsdtNum) ? totalInFlowUsdtNum : 0;
    //     if (!Array.isArray(this.fiveChartDataSets)) this.fiveChartDataSets = [];

    //     this.fiveChartDataSets[index] = {
    //       dataSet: series,
    //       totalInFlow,
    //       totalInFlowUsdt,
    //     };
    //   } catch (error) {
    //     console.error(`Lỗi khi tải dữ liệu từ API thứ ba cho ${coinName}:`, error?.message || error);
    //     if (!Array.isArray(this.fiveChartDataSets)) this.fiveChartDataSets = [];
    //     this.fiveChartDataSets[index] = {
    //       dataSet: Array(30).fill(0),
    //       totalInFlow: 0,
    //       totalInFlowUsdt: 0,
    //     };
    //   }
    // },

    // async fetchSixApiDataForCoin(coinName, index) {
    //   try {
    //     const requestBody = {
    //       symbol: this.normalizeCoinName(coinName),
    //       intervalEnum: "FIVE_MINUTES",
    //       size: "288",
    //     };

    //     const response = await fetch(`${API_URL}/binance/24hour-large-inflow`, {
    //       method: "POST",
    //       headers: { "Content-Type": "application/json" },
    //       body: JSON.stringify(requestBody),
    //     });

    //     const data = await response.json();
    //     const result = data.data
    //       .map((item) => {
    //         if (item.startTime) {
    //           return {
    //             x: item.startTime,
    //             y: parseFloat(item.netCapitalInflowVolume),
    //           };
    //         }
    //         return null;
    //       })
    //       .filter((item) => item !== null);
    //     this.sixChartDataSets[index] = result;

    //   } catch (error) {
    //     this.sixChartDataSets[index] = [];
    //     console.error(
    //       `Lỗi khi tải dữ liệu từ API thứ ba cho ${coinName}:`,
    //       error.message || error
    //     );
    //   }
    // },

    // async fetchSevenApiDataForCoin(coinName, index) {
    //   try {
    //     const requestBody = {
    //       symbol: coinName,
    //       period: this.periods
    //     };

    //     const response = await fetch(`${API_URL}/binance/get-funding-rate`, {
    //       method: "POST",
    //       headers: { "Content-Type": "application/json" },
    //       body: JSON.stringify(requestBody),
    //     });

    //     if (!response.ok) {
    //       throw new Error(`HTTP ${response.status} ${response.statusText}`);
    //     }

    //     const raw = await response.json();
    //     const list = Array.isArray(raw) ? raw : (Array.isArray(raw?.data) ? raw.data : []);

    //     const result = list
    //       .map(it => {
    //         const t = new Date(it.syncTime).getTime();
    //         const y = parseFloat(it.fundingRate);
    //         if (!Number.isFinite(t) || !Number.isFinite(y)) return null;
    //         return { x: t, y };
    //       })
    //       .filter(Boolean)
    //       .sort((a, b) => a.x - b.x);
    //     this.sevenChartDataSets[index] = result;

    //   } catch (error) {
    //     this.sevenChartDataSets[index] = [];
    //     console.error(
    //       `Lỗi khi tải dữ liệu từ API thứ ba cho ${coinName}:`,
    //       error.message || error
    //     );
    //   }
    // },

    createCharts() {
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          const kill = c => { try { c && !c._destroyed && c.destroy(); } catch (_) { /* ignore */ } };
          [this.charts, this.secondCharts, this.thirdCharts, this.fourCharts, this.fiveCharts, this.sixCharts, this.sevenCharts]
            .forEach(arr => {
              (arr || []).forEach((c, i) => {
                const cv = c?.canvas;
                if (!cv || !document.body.contains(cv)) { kill(c); arr[i] = null; }
              });
            });
          let periods = this.periods;
          this.chartIds.slice(0, this.totalChart).forEach((chartId, index) => {
            const canvas = document.getElementById(chartId);
            if (canvas) {
              if (!canvas) return;
              if (canvas.offsetParent === null) return;
              const exist = ChartJS.getChart(canvas);
              if (exist) {
                try { exist.destroy(); } catch (e) {
                  // skip
                }
              }
              const ctx = canvas.getContext("2d");
              if (!ctx) return;
              if (this.charts[index]) {
                try { this.charts[index].destroy(); } catch (e) {
                  // skip
                }
                this.charts[index] = null;
              }
              const dataSet = this.chartDataSets[index] || [];
              const labels = dataSet.map(item => item.x);
              const oi = dataSet.map(item => item.oi);
              const notional = dataSet.map(item => item.notional);

              const yL = niceMinMax(oi, 0.06);
              const yR = niceMinMax(notional, 0.06);
              const LEFT_TICKS  = 4;
              const RIGHT_TICKS = 4;
              const leftStep  = (yL.max - yL.min) / Math.max(1, LEFT_TICKS - 1);
              const rightStep = (yR.max - yR.min) / Math.max(1, RIGHT_TICKS - 1);

              this.charts[index] = new ChartJS(ctx, {
                type: "bar",
                data: {
                  labels,
                  datasets: [
                    {
                      type: 'bar',
                      label: `Open Interest (${this.getCoinName(this.coinNames[index]).replace('USDT', '')})`,
                      data: oi,
                      yAxisID: 'y',
                      backgroundColor: '#f0b90b',
                      borderColor: '#dca600',
                      borderSkipped: false,
                      borderWidth: 0,
                      borderRadius: 2,
                      barPercentage: 0.6,
                      categoryPercentage: 0.8,
                      maxBarThickness: 12,
                      datalabels: { display: false },
                      order: 1
                    },
                    {
                      type: 'line',
                      label: 'Notional Value of Open Interest (USDT)',
                      data: notional,
                      yAxisID: 'y1',
                      borderColor: '#ffffff',
                      borderWidth: 2,
                      order: 999,
                      z: 10,
                      showLine: true,
                      spanGaps: true,
                      pointRadius: 2,
                      pointHoverRadius: 3,
                      pointBackgroundColor: '#0b1220',
                      pointBorderColor: '#e5e7eb',
                      pointBorderWidth: 1,
                      tension: 0.25,
                      cubicInterpolationMode: 'monotone',
                      fill: false,
                      datalabels: { display: false }
                    }
                  ],
                },
                options: {
                  responsive: true,
                  maintainAspectRatio: false,
                  layout: { padding: 0 },
                  interaction: {
                    mode: 'index',
                    intersect: false
                  },
                  plugins: {
                    legend: {
                      display: true,
                      reverse: false
                    },
                    datalabels: {
                      display: false,
                    },
                    title: {
                      display: true,
                      text: `Open Interest(${this.getCoinName(this.coinNames[index])})`,
                    },
                    tooltip: {
                      backgroundColor: '#111827',
                      borderColor: '#374151',
                      borderWidth: 1,
                      titleColor: '#e5e7eb',
                      bodyColor: '#e5e7eb',
                      callbacks: {
                        title(items) {
                          const ts = items[0]?.parsed.x;
                          return ts ? new Date(ts).toLocaleTimeString() : '';
                        },
                        label(c) {
                          const name = c.dataset.label;
                          const value = c.parsed.y;
                          return ` ${name}: ${fmtComma(value)}`;
                        }
                      }
                    },
                  },
                  scales: {
                    x: {
                      type: 'time',
                      time: {
                        unit: this.periods === "1h" || this.periods === "4h" ? "hour"
                            : this.periods === "1d" ? "day" : "minute"
                      },
                      grid: {
                        color: 'rgba(255,255,255,0.10)',
                        drawTicks: true,
                        drawOnChartArea: true
                      },
                      offset: false,
                      bounds: 'data',
                      ticks: {
                        color: '#9ca3af',
                        maxTicksLimit: 15,
                        count: 15,
                        autoSkip: true,
                        autoSkipPadding: 8,

                        maxRotation: 55,
                        minRotation: 35,
                        padding: 2,

                        callback: function (value) {
                          const date = new Date(value);
                          if (periods === "1d") {
                            return date.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit" });
                          } else {
                            return date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
                          }
                        },
                      },
                    },
                    y: {
                      position: 'left',
                      min: yL.min,
                      max: yL.max,
                      beginAtZero: false,
                      grid: { color: 'rgba(255,255,255,0.10)', drawOnChartArea: true, drawTicks: true },
                      ticks: {
                        color: '#9ca3af', font: { size: 11 }, padding: 0, count: LEFT_TICKS, stepSize: leftStep,
                        callback: function (v) {
                          const a = Math.abs(v);
                          if (a >= 1e12) return (v / 1e12).toFixed(0) + 'T';
                          if (a >= 1e9) return (v / 1e9).toFixed(0) + 'B';
                          if (a >= 1e6) return (v / 1e6).toFixed(0) + 'M';
                          if (a >= 1e3) return (v / 1e3).toFixed(0) + 'K';
                          return Math.round(v);
                        }
                      },
                      title: { display: false }
                    },
                    y1: {
                      position: 'right',
                      min: yR.min,
                      max: yR.max,
                      beginAtZero: false,
                      grid: { display: false, drawOnChartArea: false },
                      ticks: {
                        color: '#9ca3af', font: { size: 11 }, padding: 0, count: RIGHT_TICKS, stepSize: rightStep,
                        callback: function (v) {
                          const a = Math.abs(v);
                          if (a >= 1e12) return (v / 1e12).toFixed(0) + 'T';
                          if (a >= 1e9) return (v / 1e9).toFixed(0) + 'B';
                          if (a >= 1e6) return (v / 1e6).toFixed(0) + 'M';
                          if (a >= 1e3) return (v / 1e3).toFixed(0) + 'K';
                          return Math.round(v);
                        }
                      },
                      title: { display: false }
                    }
                  }
                },
                plugins: [stripeBg]
              });
              if (canvas && canvas.parentNode) {
                canvas.parentNode.style.height = '300px';
              }
            }
          });

          this.secondChartIds
            .slice(0, this.totalChart)
            .forEach((chartId, index) => {
              const canvas = document.getElementById(chartId);
              if (!canvas) return;
              if (canvas.offsetParent === null) return;
              const exist = ChartJS.getChart(canvas);
              if (exist) {
                try { exist.destroy(); } catch (e) {
                  // skip
                }
              }
              const ctx = canvas.getContext("2d");
              if (!ctx) return;
              if (canvas) {
                if (this.secondCharts[index]) {
                  try { this.secondCharts[index].destroy(); } catch (e) {
                    // skip
                  }
                  this.secondCharts[index] = null;
                }

                const dataSet = this.secondChartDataSets[index] || [];
                const minValue = Math.min(
                  ...dataSet.map((item) => item.longShortRatio)
                );
                const roundedValue = Math.floor(minValue / 0.05) * 0.05;
                const startValue =
                  minValue - roundedValue <= 0.05
                    ? roundedValue
                    : roundedValue + 0.05;

                this.secondCharts[index] = new ChartJS(ctx, {
                  type: "bar",
                  data: {
                    datasets: [
                      {
                        label: "Tỷ lệ Long/Short (Tài khoản)",
                        type: "line",
                        data: dataSet,
                        borderColor: "rgba(0, 0, 0, 1)",
                        pointBackgroundColor: "rgba(255, 255, 255, 1)",
                        pointBorderColor: "rgba(0, 0, 0, 1)",
                        pointBorderWidth: 1.5,
                        pointRadius: 4,
                        pointHoverRadius: 6,
                        fill: true,
                        tension: 0.4,
                        borderWidth: 2,
                        yAxisID: "y-line",
                        meta: {
                          customValue: dataSet,
                        },
                      },
                      {
                        label: "% Tài khoản Long",
                        data: dataSet.map((item) => item.longPercent),
                        backgroundColor: "rgba(0, 209, 112, 0.8)",
                        stack: "Stack 0",
                        yAxisID: "y-bar",
                        meta: {
                          customValue: dataSet,
                        },
                      },
                      {
                        label: "% Tài khoản Short",
                        data: dataSet.map((item) => item.shortPercent),
                        backgroundColor: "rgba(231, 76, 60, 0.8)",
                        stack: "Stack 0",
                        yAxisID: "y-bar",
                        meta: {
                          customValue: dataSet,
                        },
                      },
                    ],
                  },
                  options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        display: true,
                      },
                      datalabels: {
                        display: false,
                      },
                      title: {
                        display: true,
                        text: `Top Accounts(${this.getCoinName(this.coinNames[index])})`,
                      },
                      tooltip: {
                        callbacks: {
                          label: function (tooltipItem) {
                            const dataset = tooltipItem.dataset;
                            const rawData = dataset.meta.customValue;
                            const index = tooltipItem.dataIndex;
                            const shortPercent = rawData[index].shortPercent || 0;
                            const longPercent = rawData[index].longPercent || 0;
                            const longShortRatio =
                              rawData[index].longShortRatio || 0;
                            return `Long: ${longPercent}%\r\nShort: ${shortPercent}%\r\nLong Short Ratio: ${Number(longShortRatio).toFixed(3)}`;
                          },
                        },
                      },
                    },
                    scales: {
                      x: {
                        type: "time",
                        time: {
                          ...(this.periods === "1d" && {
                            round: "day",
                          }),
                          unit:
                            this.periods === "1h" || this.periods === "4h"
                              ? "hour"
                              : this.periods === "1d"
                                ? "day"
                                : "minute",
                        },
                        labels: dataSet.map((item) => item.x),
                        ticks: {
                          maxTicksLimit: 20,
                          ...(this.periods !== "1d" && {
                            stepSize:
                              this.periods === "1h"
                                ? 1
                                : this.periods === "4h"
                                  ? 4
                                  : 5,
                          }),
                          callback: function (value) {
                            const date = new Date(value);
                            if (periods === "1d") {
                              return date.toLocaleDateString("en-GB", {
                                day: "2-digit",
                                month: "2-digit",
                              });
                            } else {
                              return date.toLocaleTimeString("en-GB", {
                                hour: "2-digit",
                                minute: "2-digit",
                              });
                            }
                          },
                        },
                        stacked: true,
                      },
                      "y-line": {
                        id: "y-line",
                        position: "right",
                        grid: {
                          drawOnChartArea: false,
                        },
                        beginAtZero: false,
                        min: startValue,
                        ticks: {
                          callback: function (value) {
                            return value.toFixed(2);
                          },
                        },
                      },
                      y: {
                        display: false,
                        grid: {
                          display: false,
                        },
                      },
                    },
                  },
                });
              }
            });

          this.thirdChartIds
            .slice(0, this.totalChart)
            .forEach((chartId, index) => {
              const canvas = document.getElementById(chartId);
              if (!canvas) return;
              if (canvas.offsetParent === null) return;
              const exist = ChartJS.getChart(canvas);
              if (exist) {
                try { exist.destroy(); } catch (e) {
                  // skip
                }
              }
              const ctx = canvas.getContext("2d");
              if (!ctx) return;
              if (canvas) {
                if (this.thirdCharts[index]) {
                  try { this.thirdCharts[index].destroy(); } catch (e) {
                    // skip
                  }
                  this.thirdCharts[index] = null;
                }

                const dataSet = this.thirdChartDataSets[index] || [];
                const minValue = Math.min(
                  ...dataSet.map((item) => item.longShortRatio)
                );
                const roundedValue = Math.floor(minValue / 0.05) * 0.05;
                const startValue =
                  minValue - roundedValue <= 0.05
                    ? roundedValue
                    : roundedValue + 0.05;

                this.thirdCharts[index] = new ChartJS(ctx, {
                  type: "bar",
                  data: {
                    datasets: [
                      {
                        label: "Tỷ lệ Long/Short (Vị thế)",
                        type: "line",
                        data: dataSet,
                        borderColor: "rgba(0, 0, 0, 1)",
                        pointBackgroundColor: "rgba(255, 255, 255, 1)",
                        pointBorderColor: "rgba(0, 0, 0, 1)",
                        pointBorderWidth: 1.5,
                        pointRadius: 4,
                        pointHoverRadius: 6,
                        fill: true,
                        tension: 0.4,
                        borderWidth: 2,
                        yAxisID: "y-line",
                        meta: {
                          customValue: dataSet,
                        },
                      },
                      {
                        label: "% Vị thế Long",
                        data: dataSet.map((item) => item.longPercent),
                        backgroundColor: "rgba(0, 209, 112, 0.8)",
                        stack: "Stack 0",
                        yAxisID: "y-bar",
                        meta: {
                          customValue: dataSet,
                        },
                      },
                      {
                        label: "% Vị thế Short",
                        data: dataSet.map((item) => item.shortPercent),
                        backgroundColor: "rgba(231, 76, 60, 0.8)",
                        stack: "Stack 0",
                        yAxisID: "y-bar",
                        meta: {
                          customValue: dataSet,
                        },
                      },
                    ],
                  },
                  options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        display: true,
                      },
                      datalabels: {
                        display: false,
                      },
                      title: {
                        display: true,
                        text: `Top Positions(${this.getCoinName(this.coinNames[index])})`,
                      },
                      tooltip: {
                        callbacks: {
                          label: function (tooltipItem) {
                            const dataset = tooltipItem.dataset;
                            const rawData = dataset.meta.customValue;
                            const index = tooltipItem.dataIndex;
                            const shortPercent = rawData[index].shortPercent || 0;
                            const longPercent = rawData[index].longPercent || 0;
                            const longShortRatio =
                              rawData[index].longShortRatio || 0;
                            return `Long: ${longPercent}%\r\nShort: ${shortPercent}%\r\nLong Short Ratio: ${Number(longShortRatio).toFixed(3)}`;
                          },
                        },
                      },
                    },
                    scales: {
                      x: {
                        type: "time",
                        time: {
                          ...(this.periods === "1d" && {
                            round: "day",
                          }),
                          unit:
                            this.periods === "1h" || this.periods === "4h"
                              ? "hour"
                              : this.periods === "1d"
                                ? "day"
                                : "minute",
                        },
                        labels: dataSet.map((item) => item.x),
                        ticks: {
                          maxTicksLimit: 20,
                          ...(this.periods !== "1d" && {
                            stepSize:
                              this.periods === "1h"
                                ? 1
                                : this.periods === "4h"
                                  ? 4
                                  : this.periods === "15m"
                                    ? 15
                                    : 5,
                          }),
                          callback: function (value) {
                            const date = new Date(value);
                            if (periods === "1d") {
                              return date.toLocaleDateString("en-GB", {
                                day: "2-digit",
                                month: "2-digit",
                              });
                            } else {
                              return date.toLocaleTimeString("en-GB", {
                                hour: "2-digit",
                                minute: "2-digit",
                              });
                            }
                          },
                        },
                        stacked: true,
                      },
                      "y-line": {
                        id: "y-line",
                        position: "right",
                        grid: {
                          drawOnChartArea: false,
                        },
                        beginAtZero: false,
                        min: startValue,
                        ticks: {
                          callback: function (value) {
                            return value.toFixed(2);
                          },
                        },
                      },
                      y: {
                        display: false,
                        grid: {
                          display: false,
                        },
                      },
                    },
                  },
                });
              }
            });
          this.fourChartIds
            .slice(0, this.totalChart)
            .forEach((chartId, index) => {
              const canvas = document.getElementById(chartId);
              if (!canvas) return;
              if (canvas.offsetParent === null) return;
              const exist = ChartJS.getChart(canvas);
              if (exist) {
                try { exist.destroy(); } catch (e) {
                  // skip
                }
              }
              const ctx = canvas.getContext("2d");
              if (!ctx) return;
              if (canvas) {
                if (this.fourCharts[index]) {
                  try { this.fourCharts[index].destroy(); } catch (e) {
                    // skip
                  }
                  this.fourCharts[index] = null;
                }

                const dataSet = this.fourChartDataSets[index] || [];
                const minValue = Math.min(
                  ...dataSet.map((item) => item.longShortRatio)
                );
                const roundedValue = Math.floor(minValue / 0.05) * 0.05;
                const startValue =
                  minValue - roundedValue <= 0.05
                    ? roundedValue
                    : roundedValue + 0.05;
                this.fourCharts[index] = new ChartJS(ctx, {
                  type: "bar",
                  data: {
                    datasets: [
                      {
                        label: "Tỷ lệ Long/Short",
                        type: "line",
                        data: dataSet,
                        borderColor: "rgba(0, 0, 0, 1)",
                        pointBackgroundColor: "rgba(255, 255, 255, 1)",
                        pointBorderColor: "rgba(0, 0, 0, 1)",
                        pointBorderWidth: 1.5,
                        pointRadius: 4,
                        pointHoverRadius: 6,
                        fill: true,
                        tension: 0.4,
                        borderWidth: 2,
                        yAxisID: "y-line",
                        meta: {
                          customValue: dataSet,
                        },
                      },
                      {
                        label: "% Tài khoản Long",
                        data: dataSet.map((item) => item.longPercent),
                        backgroundColor: "rgba(0, 209, 112, 0.8)",
                        stack: "Stack 0",
                        yAxisID: "y-bar",
                        meta: {
                          customValue: dataSet,
                        },
                      },
                      {
                        label: "% Tài khoản Short",
                        data: dataSet.map((item) => item.shortPercent),
                        backgroundColor: "rgba(231, 76, 60, 0.8)",
                        stack: "Stack 0",
                        yAxisID: "y-bar",
                        meta: {
                          customValue: dataSet,
                        },
                      },
                    ],
                  },
                  options: {
                    responsive: true,
                    maintainAspectRatio: false,

                    plugins: {
                      datalabels: {
                        display: false,
                      },

                      legend: {
                        display: true,
                      },
                      title: {
                        display: true,
                        text: `Accounts(${this.getCoinName(this.coinNames[index])})`,
                      },
                      tooltip: {
                        callbacks: {
                          label: function (tooltipItem) {
                            const dataset = tooltipItem.dataset;
                            const rawData = dataset.meta.customValue;
                            const index = tooltipItem.dataIndex;
                            const shortPercent = rawData[index].shortPercent || 0;
                            const longPercent = rawData[index].longPercent || 0;
                            const longShortRatio =
                              rawData[index].longShortRatio || 0;
                            return `Long: ${longPercent}%\r\nShort: ${shortPercent}%\r\nLong Short Ratio: ${Number(longShortRatio).toFixed(3)}`;
                          },
                        },
                      },
                    },
                    scales: {
                      x: {
                        type: "time",
                        time: {
                          ...(this.periods === "1d" && {
                            round: "day",
                          }),
                          unit:
                            this.periods === "1h" || this.periods === "4h"
                              ? "hour"
                              : this.periods === "1d"
                                ? "day"
                                : "minute",
                        },
                        labels: dataSet.map((item) => item.x),
                        ticks: {
                          maxTicksLimit: 20,
                          ...(this.periods !== "1d" && {
                            stepSize:
                              this.periods === "1h"
                                ? 1
                                : this.periods === "4h"
                                  ? 4
                                  : this.periods === "15m"
                                    ? 15
                                    : 5,
                          }),
                          callback: function (value) {
                            const date = new Date(value);
                            if (periods === "1d") {
                              return date.toLocaleDateString("en-GB", {
                                day: "2-digit",
                                month: "2-digit",
                              });
                            } else {
                              return date.toLocaleTimeString("en-GB", {
                                hour: "2-digit",
                                minute: "2-digit",
                              });
                            }
                          },
                        },
                        stacked: true,
                      },

                      "y-line": {
                        position: "right",
                        beginAtZero: false,
                        min: startValue,
                        ticks: {
                          callback: function (value) {
                            return value.toFixed(2);
                          },
                        },
                      },

                      y: {
                        display: false,
                        grid: {
                          display: false,
                        },
                        ticks: {
                          beginAtZero: false,
                          callback: function (value) {
                            return value.toFixed(2);
                          },
                        },
                      },
                    },
                  },
                });
              }
            });
          // this.fiveChartIds
          //   .slice(0, this.totalChart)
          //   .forEach((chartId, index) => {
          //     const canvas = document.getElementById(chartId);
          //     if (!canvas) return;
          //     if (canvas.offsetParent === null) return;
          //     const exist = ChartJS.getChart(canvas);
          //     if (exist) {
          //       try { exist.destroy(); } catch (e) {
          //         // skip
          //       }
          //     }
          //     const ctx = canvas.getContext("2d");
          //     if (!ctx) return;
          //     if (canvas) {
          //       if (this.fiveCharts[index]) {
          //         try { this.fiveCharts[index].destroy(); } catch (e) {
          //           // skip
          //         }
          //         this.fiveCharts[index] = null;
          //       }
          //       const dataGet = this.fiveChartDataSets[index] || [];
          //       const totalInFlow = Math.abs(dataGet.totalInFlow);
          //       let dataTotalInFlow = 0;
          //       if (totalInFlow >= 1000000000) {
          //         dataTotalInFlow = (totalInFlow / 1000000000).toFixed(2) + "B";
          //       } else if (totalInFlow >= 1000000) {
          //         dataTotalInFlow = (totalInFlow / 1000000).toFixed(2) + "M";
          //       } else if (totalInFlow >= 1000) {
          //         dataTotalInFlow = (totalInFlow / 1000).toFixed(2) + "K";
          //       }

          //       // Format totalInFlowUsdt
          //       const totalInFlowUsdt = Math.abs(dataGet.totalInFlowUsdt || 0);
          //       let dataTotalInFlowUsdt = 0;
          //       if (totalInFlowUsdt >= 1000000000) {
          //         dataTotalInFlowUsdt = (totalInFlowUsdt / 1000000000).toFixed(2) + "B";
          //       } else if (totalInFlowUsdt >= 1000000) {
          //         dataTotalInFlowUsdt = (totalInFlowUsdt / 1000000).toFixed(2) + "M";
          //       } else if (totalInFlowUsdt >= 1000) {
          //         dataTotalInFlowUsdt = (totalInFlowUsdt / 1000).toFixed(2) + "K";
          //       }

          //       const dataSet = dataGet.dataSet;
          //       this.fiveCharts[index] = new ChartJS(ctx, {
          //         type: "bar",
          //         data: {
          //           labels: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30],
          //           datasets: [
          //             {
          //               label: `30 days large inflow: ${dataTotalInFlow} || ${dataTotalInFlowUsdt}`,
          //               data: dataSet,
          //               backgroundColor: dataSet.map((value) =>
          //                 value < 0
          //                   ? "rgba(255, 0, 0, 0.6)"
          //                   : "rgba(0, 128, 0, 0.6)"
          //               ),
          //               borderColor: dataSet.map((value) =>
          //                 value < 0 ? "rgb(255, 0, 0)" : "rgb(0, 128, 0)"
          //               ),
          //               borderWidth: 1,
          //             },
          //           ],
          //         },
          //         options: {
          //           maintainAspectRatio: false,
          //           tooltips: { enabled: false },
          //           hover: { mode: null },
          //           responsive: true,
          //           scales: {
          //             x: {
          //               display: false,
          //               reverse: true
          //             },
          //             y: {
          //               display: true,
          //               ticks: {
          //                 callback: function (value) {
          //                   const absValue = Math.abs(value);
          //                   if (absValue >= 1000000000) {
          //                     return (absValue / 1000000000).toFixed(2) + "B";
          //                   } else if (absValue >= 1000000) {
          //                     return (absValue / 1000000).toFixed(2) + "M";
          //                   } else if (absValue >= 1000) {
          //                     return (absValue / 1000).toFixed(2) + "K";
          //                   }
          //                   return absValue;
          //                 },
          //               },
          //             },
          //           },
          //           plugins: {
          //             title: {
          //               display: true,
          //               text: `30 days large inflow: ${dataTotalInFlow} || ${dataTotalInFlowUsdt}`,
          //               font: {
          //                 style: "italic",
          //               },
          //               color: dataGet.totalInFlow < 0 ? "red" : "green",
          //               padding: 30,
          //             },

          //             tooltip: {
          //               enabled: false,
          //               callbacks: {
          //                 label: function (context) {
          //                   const value = context.raw;
          //                   const absValue = Math.abs(value);
          //                   let formattedValue = value;
          //                   if (absValue >= 1000000000) {
          //                     formattedValue = (value / 1000000000).toFixed(2) + "B"; // Tỉ
          //                   } else if (absValue >= 1000000) {
          //                     formattedValue = (value / 1000000).toFixed(2) + "M"; // Triệu
          //                   } else if (absValue >= 1000) {
          //                     formattedValue = (value / 1000).toFixed(2) + "K"; // Nghìn
          //                   }
          //                   return `${context.dataset.label}: ${formattedValue}`;
          //                 }
          //               }
          //             },
          //             legend: {
          //               display: false,
          //             },
          //             datalabels: {
          //               display: true,
          //               align: (context) => {
          //                 return context.dataset.data[context.dataIndex] < 0
          //                   ? "start"
          //                   : "end";
          //               },
          //               anchor: (context) => {
          //                 return context.dataset.data[context.dataIndex] < 0
          //                   ? "start"
          //                   : "end";
          //               },
          //               color: (context) => {
          //                 return context.dataset.data[context.dataIndex] < 0
          //                   ? "red"
          //                   : "green";
          //               },
          //               offset: 0,
          //               font: {
          //                 weight: "bold",
          //                 size: 10,
          //               },
          //               formatter: function (value) {
          //                 const absValue = Math.abs(value);
          //                 if (absValue >= 1000000000) {
          //                   return (value / 1000000000).toFixed(2) + "B";
          //                 } else if (absValue >= 1000000) {
          //                   return (value / 1000000).toFixed(2) + "M";
          //                 } else if (absValue >= 1000) {
          //                   return (value / 1000).toFixed(2) + "K";
          //                 }
          //                 return value;
          //               },
          //             },
          //           },
          //         },
          //       });
          //     }
          //   });
          // this.sixChartIds.slice(0, this.totalChart).forEach((chartId, index) => {
          //   const canvas = document.getElementById(chartId);
          //   if (!canvas) return;
          //   if (canvas.offsetParent === null) return;
          //   const exist = ChartJS.getChart(canvas);
          //   if (exist) {
          //     try { exist.destroy(); } catch (e) {
          //       // skip
          //     }
          //   }
          //   const ctx = canvas.getContext("2d");
          //   if (!ctx) return;
          //   if (canvas) {
          //     if (this.sixCharts[index]) {
          //       try { this.sixCharts[index].destroy(); } catch (e) {
          //         // skip
          //       }
          //       this.sixCharts[index] = null;
          //     }

          //     const dataSet = this.sixChartDataSets[index] || [];
          //     this.sixCharts[index] = new ChartJS(ctx, {
          //       type: "line",
          //       data: {
          //         datasets: [
          //           {
          //             data: dataSet,
          //             borderColor: "#f39c12",
          //             backgroundColor: "rgba(243, 156, 18, 0.5)",
          //             fill: true,
          //             tension: 0,
          //             borderWidth: 2,
          //             pointRadius: 0,
          //           },
          //         ],
          //       },
          //       options: {
          //         interaction: {
          //           mode: "index",
          //           intersect: false,
          //         },
          //         plugins: {
          //           datalabels: {
          //             display: false,
          //           },
          //           legend: {
          //             display: false,
          //           },
          //           title: {
          //             display: true,
          //             text: `24hr Large Inflow(${this.getCoinName(this.coinNames[index])})`,
          //           },
          //           tooltip: {
          //             enabled: true,
          //           },
          //         },
          //         scales: {
          //           x: {
          //             type: "time",
          //             grid: {
          //               display: false,
          //             },
          //             time: {
          //               unit: "hour",
          //             },
          //             labels: dataSet.map((item) => item.x),
          //             ticks: {
          //               maxTicksLimit: 30,
          //               stepSize: 6,
          //               callback: function (value) {
          //                 const date = new Date(value);
          //                 const time = date.toLocaleTimeString("en-GB", {
          //                   hour: "2-digit",
          //                   minute: "2-digit",
          //                 });
          //                 return time;
          //               },
          //             },
          //             stacked: true,
          //           },
          //           y: {
          //             display: true,
          //           },
          //         },
          //         responsive: true,
          //         maintainAspectRatio: false,
          //       },
          //     });
          //   }
          // });
          // this.sevenChartIds.slice(0, this.totalChart).forEach((chartId, index) => {
          //   const canvas = document.getElementById(chartId);
          //   if (canvas) {
          //     if (!canvas) return;
          //     if (canvas.offsetParent === null) return;
          //     const exist = ChartJS.getChart(canvas);
          //     if (exist) {
          //       try { exist.destroy(); } catch (e) {
          //         // skip
          //       }
          //     }
          //     const ctx = canvas.getContext("2d");
          //     if (!ctx) return;
          //     if (this.sevenCharts[index]) {
          //       try { this.sevenCharts[index].destroy(); } catch (e) {
          //         // skip
          //       }
          //       this.sevenCharts[index] = null;
          //     }
          //     const dataSet = this.sevenChartDataSets[index] || [];
          //     this.sevenCharts[index] = new ChartJS(ctx, {
          //       type: "line",
          //       data: {
          //         datasets: [
          //           {
          //             label: `Funding Rate (%)`,
          //             data: dataSet,
          //             borderColor: "#e91e63",
          //             backgroundColor: "rgba(233, 30, 99, 0.25)",
          //             fill: true,
          //             tension: 0,
          //             borderWidth: 2,
          //           },
          //         ],
          //       },
          //       options: {
          //         plugins: {
          //           legend: {
          //             display: true,
          //           },
          //           datalabels: {
          //             display: false,
          //           },
          //           title: {
          //             display: true,
          //             text: `Funding Rate (${this.getCoinName(this.coinNames[index])})`,
          //           },
          //           tooltip: {
          //             enabled: true,
          //             callbacks: {
          //               label: function (ctx) {
          //                 const v = ctx.parsed?.y;
          //                 if (v == null) return '';
          //                 return `Funding rate: ${Number(v).toFixed(4)}%`;
          //               }
          //             }
          //           },
          //         },
          //         interaction: {
          //           mode: "index",
          //           intersect: false,
          //         },
          //         scales: {
          //           x: {
          //             type: "time",
          //             time: {
          //               ...(this.periods === "1d" && {
          //                 round: "day",
          //               }),
          //               unit:
          //                 this.periods === "1h" || this.periods === "4h"
          //                   ? "hour"
          //                   : this.periods === "1d"
          //                     ? "day"
          //                     : "minute",
          //             },
          //             labels: dataSet.map((item) => item.x),
          //             ticks: {
          //               maxTicksLimit: 20,
          //               ...(this.periods !== "1d" && {
          //                 stepSize:
          //                   this.periods === "1h"
          //                     ? 1
          //                     : this.periods === "4h"
          //                       ? 4
          //                       : 5,
          //               }),
          //               callback: (val) => {
          //                 const ts = typeof val === 'number' ? val : Date.parse(val);
          //                 if (!Number.isFinite(ts)) return String(val ?? "");
          //                 const d = new Date(ts);

          //                 const tz = 'Asia/Ho_Chi_Minh';
          //                 if (periods === "1d") {
          //                   return new Intl.DateTimeFormat('en-GB', {
          //                     timeZone: tz, day: '2-digit', month: '2-digit'
          //                   }).format(d);
          //                 }

          //                 const isHourUnit = periods === "1h" || periods === "4h" || periods === "8h";
          //                 return new Intl.DateTimeFormat('en-GB', {
          //                   timeZone: tz,
          //                   hour: '2-digit',
          //                   minute: isHourUnit ? undefined : '2-digit'
          //                 }).format(d);
          //               },
          //             },
          //             stacked: true,
          //           },
          //           y: {
          //             ticks: {
          //               callback: (v) => `${Number(v).toFixed(4)}%`,
          //             },
          //           },
          //         },
          //         responsive: true,
          //         maintainAspectRatio: false,
          //       },
          //     });
          //   }
          // });
        });
      });
    },
    handleMetrics(list) {
      this.selectedMetrics = Array.isArray(list) ? list : [];
    }
  },
  beforeUnmount() {
    const killArr = (arr) => (arr || []).forEach(c => {
      try { c?.destroy(); } catch (e) {
        // skip
      }
    });
    killArr(this.charts);
    killArr(this.secondCharts);
    killArr(this.thirdCharts);
    killArr(this.fourCharts);
    killArr(this.fiveCharts);
    killArr(this.sixCharts);

    this.charts = this.secondCharts = this.thirdCharts =
      this.fourCharts = this.fiveCharts = this.sixCharts = [];
  },

  deactivated() {
    const killArr = (arr) => (arr || []).forEach(c => {
      try { c?.destroy(); } catch (e) {
        // skip
      }
    });
    killArr(this.charts);
    killArr(this.secondCharts);
    killArr(this.thirdCharts);
    killArr(this.fourCharts);
    killArr(this.fiveCharts);
    killArr(this.sixCharts);
  }
};
</script>
